import { useEffect, useRef, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Plus, Send, Trash2, Hash, FolderKanban, MessagesSquare } from "lucide-react";

export default function SpacesPage() {
  const { user } = useAuth();
  const [spaces, setSpaces] = useState(null);
  const [selected, setSelected] = useState(null);
  const [newOpen, setNewOpen] = useState(false);

  const load = async (keepId) => {
    try {
      const { data } = await api.get("/spaces");
      setSpaces(data);
      setSelected((prev) => {
        const id = keepId || prev;
        return data.some((s) => s.id === id) ? id : data[0]?.id || null;
      });
    } catch (e) {
      toast.error("Could not load spaces");
      setSpaces([]);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!spaces) return <Layout><div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div></Layout>;
  const space = spaces.find((s) => s.id === selected);
  const general = spaces.filter((s) => s.type === "general");
  const projectSpaces = spaces.filter((s) => s.type === "project");

  return (
    <Layout>
      <div className="space-y-6" data-testid="spaces-page">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-3xl font-extrabold tracking-tight">Orchestra Pit</h1>
            <p className="text-sm text-muted-foreground mt-1">Talk through the details with your team — general spaces or per-project rooms.</p>
          </div>
          <Button className="gap-1.5" onClick={() => setNewOpen(true)} data-testid="new-space-button"><Plus className="h-4 w-4" /> New space</Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
          <div className="space-y-5">
            <SpaceGroup label="Team spaces" icon={MessagesSquare} items={general} selected={selected} onSelect={setSelected} />
            <SpaceGroup label="Project spaces" icon={FolderKanban} items={projectSpaces} selected={selected} onSelect={setSelected} />
            {spaces.length === 0 && <p className="text-sm text-muted-foreground border border-dashed border-border rounded-lg p-4">No spaces yet. Create the first one to get the conversation going.</p>}
          </div>
          {space ? <ChatPanel key={space.id} space={space} user={user} onDeleted={() => load()} /> : (
            <div className="border border-border rounded-xl flex items-center justify-center min-h-[400px] text-sm text-muted-foreground">Select a space to start chatting</div>
          )}
        </div>
      </div>
      <NewSpaceDialog open={newOpen} onOpenChange={setNewOpen} onCreated={(s) => { setNewOpen(false); load(s.id); }} />
    </Layout>
  );
}

function SpaceGroup({ label, icon: Icon, items, selected, onSelect }) {
  if (items.length === 0) return null;
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5"><Icon className="h-3.5 w-3.5" /> {label}</p>
      <div className="space-y-1">
        {items.map((s) => (
          <button key={s.id} onClick={() => onSelect(s.id)}
            className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center gap-2 ${s.id === selected ? "bg-secondary text-foreground font-semibold" : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"}`}
            data-testid={`space-item-${s.id}`}>
            <Hash className="h-3.5 w-3.5 shrink-0 opacity-60" />
            <span className="truncate flex-1">{s.name}</span>
            {s.message_count > 0 && <span className="text-[10px] tabular-nums opacity-60">{s.message_count}</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

function ChatPanel({ space, user, onDeleted }) {
  const [messages, setMessages] = useState(null);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const firstLoad = useRef(true);

  useEffect(() => {
    api.get("/mention-candidates").then(({ data }) => setCandidates(data)).catch(() => {});
  }, []);

  const mentionMatch = body.match(/@([^@\n]{0,30})$/);
  const mentionQuery = mentionMatch ? mentionMatch[1].toLowerCase() : null;
  const mentionOptions = mentionQuery !== null
    ? candidates.filter((n) => n.toLowerCase().startsWith(mentionQuery)).slice(0, 6)
    : [];

  const pickMention = (name) => {
    setBody((b) => b.replace(/@([^@\n]{0,30})$/, `@${name} `));
  };

  const fetchMessages = useCallback(async () => {
    try {
      const { data } = await api.get(`/spaces/${space.id}/messages`);
      setMessages(data);
    } catch (err) { console.debug("Space messages poll failed", err); }
  }, [space.id]);

  useEffect(() => {
    fetchMessages();
    const t = setInterval(fetchMessages, 5000);
    return () => clearInterval(t);
  }, [fetchMessages]);

  useEffect(() => {
    if (messages && firstLoad.current) { bottomRef.current?.scrollIntoView(); firstLoad.current = false; }
    else if (messages) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- scroll only when the message COUNT changes, not on every poll refresh
  }, [messages?.length]);

  const send = async () => {
    const text = body.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const mentions = candidates.filter((n) => text.toLowerCase().includes("@" + n.toLowerCase()));
      await api.post(`/spaces/${space.id}/messages`, { body: text, mentions });
      setBody("");
      await fetchMessages();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSending(false); }
  };

  const deleteMessage = async (mid) => {
    try {
      await api.delete(`/spaces/${space.id}/messages/${mid}`);
      setMessages((prev) => prev.filter((m) => m.id !== mid));
    } catch (e) { toast.error("Could not delete message"); }
  };

  const deleteSpace = async () => {
    if (!window.confirm(`Delete the "${space.name}" space and all its messages?`)) return;
    try {
      await api.delete(`/spaces/${space.id}`);
      toast.success("Space deleted");
      onDeleted();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const canDeleteSpace = space.created_by === user?.id || user?.role === "admin";

  return (
    <div className="border border-border rounded-xl flex flex-col min-h-[500px] max-h-[calc(100vh-260px)]" data-testid="space-chat-panel">
      <div className="px-5 py-3.5 border-b border-border flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold flex items-center gap-1.5 truncate" data-testid="space-title"><Hash className="h-4 w-4 opacity-60" /> {space.name}</p>
          {(space.project_name || space.description) && (
            <p className="text-xs text-muted-foreground truncate mt-0.5">{space.project_name ? `Project: ${space.project_name}` : space.description}</p>
          )}
        </div>
        {canDeleteSpace && (
          <Button variant="ghost" size="icon" onClick={deleteSpace} data-testid="delete-space-button" title="Delete space">
            <Trash2 className="h-4 w-4 text-muted-foreground" />
          </Button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4" data-testid="space-messages">
        {!messages && <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>}
        {messages && messages.length === 0 && <p className="text-sm text-muted-foreground text-center py-10">No messages yet — say hello 👋</p>}
        {messages && messages.map((m) => {
          const mine = m.user_id === user?.id;
          return (
            <div key={m.id} className={`group flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`message-${m.id}`}>
              <div className={`max-w-[75%] rounded-xl px-4 py-2.5 relative ${mine ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>
                <div className="flex items-baseline gap-2 mb-0.5">
                  <span className={`text-xs font-semibold ${mine ? "text-primary-foreground/90" : "text-foreground"}`}>{mine ? "You" : m.user_name}</span>
                  <span className={`text-[10px] ${mine ? "text-primary-foreground/60" : "text-muted-foreground"}`}>{formatTime(m.created_at)}</span>
                </div>
                <p className="text-sm whitespace-pre-wrap break-words">{renderMessageBody(m, mine)}</p>
                {(mine || user?.role === "admin") && (
                  <button onClick={() => deleteMessage(m.id)} data-testid={`delete-message-${m.id}`}
                    className="absolute -top-2 -right-2 flex md:opacity-0 md:group-hover:opacity-100 h-5 w-5 rounded-full bg-background border border-border items-center justify-center hover:bg-destructive hover:text-destructive-foreground transition-opacity">
                    <Trash2 className="h-3 w-3" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      <div className="border-t border-border p-3 relative">
        {mentionOptions.length > 0 && (
          <div className="absolute bottom-full left-3 mb-1 w-64 rounded-lg border border-border bg-popover shadow-lg overflow-hidden z-10" data-testid="mention-dropdown">
            <p className="px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Mention someone</p>
            {mentionOptions.map((n) => (
              <button key={n} onClick={() => pickMention(n)}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-secondary transition-colors"
                data-testid={`mention-option-${n.toLowerCase().replace(/\s+/g, "-")}`}>
                @{n}
              </button>
            ))}
          </div>
        )}
        <div className="flex items-end gap-2">
          <Textarea ref={inputRef} value={body} onChange={(e) => setBody(e.target.value)} rows={1}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && mentionOptions.length === 0) { e.preventDefault(); send(); } }}
            placeholder={`Message #${space.name} — type @ to mention someone`} className="resize-none min-h-[42px] max-h-32" data-testid="message-input" />
          <Button onClick={send} disabled={!body.trim() || sending} size="icon" className="shrink-0 h-[42px] w-[42px]" data-testid="send-message-button">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}

function NewSpaceDialog({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [type, setType] = useState("general");
  const [projectId, setProjectId] = useState("");
  const [projects, setProjects] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {});
  }, [open]);

  const create = async () => {
    if (!name.trim()) return toast.error("Give the space a name");
    if (type === "project" && !projectId) return toast.error("Pick a project for this space");
    setSaving(true);
    try {
      const { data } = await api.post("/spaces", { name: name.trim(), description: description.trim(), project_id: type === "project" ? projectId : null });
      toast.success("Space created");
      setName(""); setDescription(""); setType("general"); setProjectId("");
      onCreated(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="new-space-dialog">
        <DialogHeader><DialogTitle>Create a space</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Launch war-room" data-testid="space-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="space-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="general">General team space (everyone)</SelectItem>
                <SelectItem value="project">Project space</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {type === "project" && (
            <div className="space-y-1.5">
              <Label>Project</Label>
              <Select value={projectId} onValueChange={setProjectId}>
                <SelectTrigger data-testid="space-project-select"><SelectValue placeholder="Pick a project" /></SelectTrigger>
                <SelectContent>
                  {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Description (optional)</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder="What is this space for?" data-testid="space-description-input" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={create} disabled={saving} data-testid="create-space-submit">{saving && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />} Create space</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function renderMessageBody(m, mine) {
  const names = (m.mentions || []).map((x) => x.person).filter(Boolean);
  if (!names.length) return m.body;
  const re = new RegExp(`@(${names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`, "gi");
  const parts = m.body.split(re);
  return parts.map((p, i) =>
    i % 2 === 1
      ? <span key={`${i}-${p}`} className={`font-bold rounded px-0.5 ${mine ? "bg-primary-foreground/20" : "bg-violet-100 text-violet-800"}`}>@{p}</span>
      : p
  );
}

function formatTime(iso) {
  try {
    const d = new Date(iso);
    const today = new Date();
    const sameDay = d.toDateString() === today.toDateString();
    return sameDay ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : d.toLocaleDateString([], { month: "short", day: "numeric" }) + " " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}
